import type { ReactNode } from 'react';
import { Accordion, cn } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { AccordionBodyContainer, BoldText, SummaryTitleRow } from './styles';
import type {
  OrderSalesProceeds,
  OrderSummary,
} from '@skupreme/api-contracts/core';

type SummaryProps = {
  currency?: string;
  customTotal?: number;
  marketplace?: string;
  salesProceeds?: OrderSalesProceeds;
  summary?: OrderSummary;
};

const PAIR = 'sk:grid sk:grid-cols-2 sk:gap-2';

function Pair({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(PAIR, className)}>
      <div>{label}</div>
      <BoldText>{children}</BoldText>
    </div>
  );
}

function formatDate(date?: string) {
  return !date ? '-' : DateUtility.formatDate(date, DateFormat.date);
}

export function Summary({
  summary,
  marketplace,
  salesProceeds,
  customTotal = 0,
  currency,
}: SummaryProps) {
  return (
    <Accordion title="Summary" defaultExpanded headingLevel={4}>
      <AccordionBodyContainer>
        <div className="sk:grid sk:grid-cols-2 sk:gap-3">
          <div className="sk:flex sk:flex-col sk:gap-1">
            <div>Store</div>
            <Pair label="Order Date">{formatDate(summary?.purchaseDate)}</Pair>
            <Pair label="DeliverBy">{formatDate(summary?.deliverBy)}</Pair>
            <Pair label="Ship By">{formatDate(summary?.shipBy)}</Pair>
          </div>
          <div className="sk:flex sk:flex-col sk:gap-1">
            <BoldText>{marketplace}</BoldText>
            <Pair label="Product Total">
              {CommonUtility.currencyFormat(
                (salesProceeds?.items_total ?? 0) + customTotal,
                currency,
              )}
            </Pair>
            <Pair label="Shipping Paid">
              {CommonUtility.currencyFormat(
                salesProceeds?.shipping_total,
                currency,
              )}
            </Pair>
            <Pair label="Tax Paid">
              {CommonUtility.currencyFormat(salesProceeds?.tax_total, currency)}
            </Pair>
            <SummaryTitleRow>
              <Pair label="Total Order">
                {CommonUtility.currencyFormat(
                  (salesProceeds?.grand_total ?? 0) + customTotal,
                  currency,
                )}
              </Pair>
            </SummaryTitleRow>
            <Pair label="Total Paid">
              {CommonUtility.currencyFormat(
                (salesProceeds?.grand_total ?? 0) + customTotal,
                currency,
              )}
            </Pair>
          </div>
        </div>
      </AccordionBodyContainer>
    </Accordion>
  );
}
